import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useBean } from '../context/BeanContext';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { usePet } from '../context/PetContext';
import { useLanguage } from '../functions/useLanguage';
import { BEAN_LEVEL_THRESHOLDS, CompanionEntry, PET_COLORS, STORY_CHIPS, STORY_TEXT_MAX_LEN } from '../types';
import { Modal } from '../components/Modal';
import BeanIcon from '../components/Custom Icons/KodaIcon';
import { SpeciesGlyph } from '../components/PetSwitcher';
import { ChevronUp, Bone, Trophy, Megaphone, HousePlus, BedDouble, Sparkles, Cake, Ham, Stethoscope, Scissors, Syringe, PartyPopper } from 'lucide-react';
import type { TranslationKey } from '../functions/i18n';
import { cn } from '../lib/utils';

// ─── Constants ────────────────────────────────────────────────────────────────

const STORY_CHIP_ICONS: Record<string, React.ElementType> = {
  newHome:      HousePlus,
  restingUp:    BedDouble,
  feelingGreat: Sparkles,
  birthday:     Cake,
  newTreat:     Ham,
  vetVisit:     Stethoscope,
  grooming:     Scissors,
  vaccinated:   Syringe,
  recovered:    PartyPopper,
};

const LEVEL_NAME_KEYS: TranslationKey[] = [
  'bean.levelName.seedling', 'bean.levelName.sprouting', 'bean.levelName.budding', 'bean.levelName.rooted',
  'bean.levelName.thriving', 'bean.levelName.flourishing', 'bean.levelName.resilient',
  'bean.levelName.champion', 'bean.levelName.guardian', 'bean.levelName.legend',
];

const COMPANION_REACTIONS = ['❤️', '💪', '🔥', '👏', '🌟'];

// ─── Sub-components ───────────────────────────────────────────────────────────

function BeanAvatar({ color, size = 56 }: { color: string; size?: number }) {
  return (
    <BeanIcon
      beanColor={color}
      width={size}
      height={size}
      className="shrink-0"
    />
  );
}

function XPBar({ xp, level, color }: { xp: number; level: number; color: string }) {
  const currentThreshold = BEAN_LEVEL_THRESHOLDS[level - 1] ?? 0;
  const nextThreshold = BEAN_LEVEL_THRESHOLDS[level] ?? xp;
  const progress = Math.min(((xp - currentThreshold) / (nextThreshold - currentThreshold)) * 100, 100);

  return (
    <div className="w-full">
      <div className="flex justify-between text-xs mb-1" style={{ color: color + '99' }}>
        <span>{xp} XP</span>
        <span>{nextThreshold} XP</span>
      </div>
      <div className="w-full h-3 rounded-full overflow-hidden" style={{ background: color + '22' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${progress}%`, background: color }}
        />
      </div>
    </div>
  );
}

function StreakCalendar({ logs, color }: { logs: { date: string }[]; color: string }) {
  const logDates = new Set(logs.map(l => l.date));
  const todayISO = new Date().toISOString().split('T')[0];
  const days: { date: string; day: number; active: boolean; isToday: boolean }[] = [];

  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const iso = d.toISOString().split('T')[0];
    days.push({ date: iso, day: d.getDate(), active: logDates.has(iso), isToday: iso === todayISO });
  }

  return (
    <div className="flex flex-wrap gap-1 mt-3">
      {days.map(({ date, day, active, isToday }) => (
        <div
          key={date}
          title={date}
          className="w-6 h-6 rounded-md transition-colors flex items-center justify-center"
          style={{ background: active ? color : isToday ? color + '44' : color + '22' }}
        >
          <span
            className="text-[9px] font-bold leading-none"
            style={{
              color: active ? 'white' : isToday ? color : color + '88',
              textDecoration: (active || isToday) ? 'none' : 'line-through',
            }}
          >
            {day}
          </span>
        </div>
      ))}
    </div>
  );
}

function CompanionRow({
  entry,
  rank,
  isMe,
  currentUserId,
  onReact,
  onStoryEdit,
  showWeek,
  t,
}: {
  entry: CompanionEntry;
  rank: number;
  isMe: boolean;
  currentUserId: string;
  onReact: (companionDocId: string, emoji: string) => void;
  onStoryEdit?: () => void;
  showWeek?: boolean;
  t: (key: TranslationKey) => string;
}) {
  const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : null;

  // Resolve story chip
  const chip = (() => {
    if (!entry.story_chip) return null;
    const def = STORY_CHIPS.find(c => c.key === entry.story_chip);
    if (!def || def.key === 'none') return null;
    return def;
  })();

  return (
    <div className={`py-3 border-b border-gray-50 last:border-0 ${isMe ? 'bg-primary-brown/5 rounded-2xl px-2 -mx-2' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 shrink-0 text-center pt-2">
          {medal ? (
            <span className="text-xl leading-none">{medal}</span>
          ) : (
            <span className="text-sm font-bold text-gray-400">{rank}</span>
          )}
        </div>
        <div className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center" style={{ background: entry.pet_color + '22' }}>
          <SpeciesGlyph species={entry.pet_species ?? 'otheranimal'} color={entry.pet_color} className="w-7 h-7" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
            <p className="text-sm font-bold text-text-primary truncate">
              {entry.pet_name}
              {isMe && <span className="ml-1 text-xs font-semibold text-primary-brown">({t('companions.you')})</span>}
            </p>
            {chip && (() => {
              const ChipIcon = STORY_CHIP_ICONS[chip.key];
              return (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 inline-flex items-center gap-1">
                  {ChipIcon && <ChipIcon size={11} aria-hidden />}
                  <span>{t(`companions.story.${chip.key}` as TranslationKey)}</span>
                </span>
              );
            })()}
            <div className="flex items-center flex-wrap gap-1">
              {COMPANION_REACTIONS.map(emoji => {
                const reactors: string[] = entry.reactions?.[emoji] ?? [];
                const count = reactors.length;
                const reacted = reactors.includes(currentUserId);
                if (isMe) {
                  if (count === 0) return null;
                  return (
                    <span key={emoji} className="text-xs px-1.5 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-text-secondary">
                      {emoji} {count}
                    </span>
                  );
                }
                return (
                  <button
                    key={emoji}
                    onClick={() => onReact(entry.id, emoji)}
                    className={`text-xs px-1.5 py-0.5 rounded-full border transition-colors ${reacted ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-gray-50 border-gray-200 text-text-secondary hover:border-blue-200'}`}
                  >
                    {emoji}{count > 0 && <span className="ml-0.5">{count}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {showWeek
              ? `${t('companions.weekXPShort').replace('{n}', String(entry.week_score ?? 0))} · ${t('companions.streakShort').replace('{n}', String(entry.current_streak))}`
              : `${t('companions.levelShort').replace('{n}', String(entry.level))} · ${t('companions.streakShort').replace('{n}', String(entry.current_streak))}`}
          </p>
        </div>
        {isMe && onStoryEdit && (
          <button
            onClick={onStoryEdit}
            className="shrink-0 mt-1 p-1.5 rounded-full hover:bg-primary-brown/10 transition-colors"
            title={t('companions.story.edit')}
            aria-label={t('companions.story.edit')}
          >
            <Megaphone size={16} style={{ color: entry.pet_color }} />
          </button>
        )}
      </div>
      {entry.story_text && (
        <p className="mt-1.5 ml-[44px] text-xs text-text-secondary leading-snug break-words">
          {entry.story_text}
        </p>
      )}
    </div>
  );
}

function CelebrationModal({ onClose }: { onClose: () => void }) {
  const { pendingCelebration } = useBean();
  const { t } = useLanguage();
  if (!pendingCelebration) return null;

  const isLevelUp = pendingCelebration.type === 'level_up';
  const currentLevel = isLevelUp ? pendingCelebration.level : 1;
  const levelName = t(LEVEL_NAME_KEYS[currentLevel - 1] ?? 'bean.levelName.legend');
  const title = isLevelUp
    ? t('bean.celebration.levelUpTitle').replace('{level}', String(currentLevel))
    : t('bean.celebration.streakTitle').replace('{streak}', String(pendingCelebration.streak));
  const body = isLevelUp
    ? t('bean.celebration.levelUpBody').replace('{name}', levelName)
    : t('bean.celebration.streakBody').replace('{streak}', String(pendingCelebration.streak));

  return (
    <Modal open={true} title="" onClose={onClose}>
      <div className="flex flex-col items-center text-center gap-4 p-2">
        <div className="w-32 h-32 rounded-full bg-blue-50 flex items-center justify-center text-6xl">
          {isLevelUp ? '🌟' : '🔥'}
        </div>
        <h2 className="text-2xl font-black text-[#0d2744]">{title}</h2>
        <p className="text-text-secondary text-sm">{body}</p>
        <button
          onClick={onClose}
          className="mt-2 text-white font-bold px-8 py-3 rounded-full transition-opacity hover:opacity-90"
          style={{ background: '#1976d2' }}
        >
          {t('bean.keepGoing')}
        </button>
      </div>
    </Modal>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function KodaFellowship() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { activePet, activePetOwnerUid, pets, petsLoading } = usePet();
  const {
    profile,
    companions,
    companionsWeek,
    pendingCelebration,
    clearCelebration,
    reactToCompanion,
    setStory,
    levelFromXP,
    setPublicView,
  } = useBean();

  const { logs } = useData();
  const [savingPublic, setSavingPublic] = useState(false);

  const handlePublicToggle = async () => {
    if (savingPublic) return;
    setSavingPublic(true);
    try { await setPublicView(!isPublic); }
    finally { setSavingPublic(false); }
  };
  const loggedDates = useMemo(() => {
    const byDate = new Map<string, { hasHR: boolean; hasRR: boolean; hasTemp: boolean }>();
    for (const l of logs) {
      const date = l.timestamp.split('T')[0];
      const entry = byDate.get(date) ?? { hasHR: false, hasRR: false, hasTemp: false };
      if (l.heart_rate_bpm != null) entry.hasHR = true;
      if (l.resp_rate_bpm != null) entry.hasRR = true;
      if (l.temp_c != null) entry.hasTemp = true;
      byDate.set(date, entry);
    }
    return Array.from(byDate.entries())
      .filter(([, e]) => e.hasHR && e.hasRR && e.hasTemp)
      .map(([date]) => ({ date }));
  }, [logs]);

  // Compute streak from the same vitals data the calendar uses so they always agree.
  // If today hasn't been fully logged yet, seed from yesterday — the streak is still alive.
  const computedStreak = useMemo(() => {
    const dates = new Set(loggedDates.map(l => l.date));
    const today = new Date().toISOString().split('T')[0];
    const d = new Date();
    if (!dates.has(today)) {
      // Today not yet logged — seed from yesterday so an unbroken streak isn't shown as 0
      d.setDate(d.getDate() - 1);
    }
    let streak = 0;
    while (true) {
      const iso = d.toISOString().split('T')[0];
      if (!dates.has(iso)) break;
      streak++;
      d.setDate(d.getDate() - 1);
    }
    return streak;
  }, [loggedDates]);

  const myRowRef = useRef<HTMLDivElement>(null);

  const [showScrollTop, setShowScrollTop] = useState(false);
  const [companionTab, setCompanionTab] = useState<'all' | 'week'>('all');
  const [storyModalOpen, setStoryModalOpen] = useState(false);
  const [draftChip, setDraftChip] = useState<string | null>(null);
  const [draftText, setDraftText] = useState('');
  const [savingStory, setSavingStory] = useState(false);

  // Must be declared before any early return to satisfy Rules of Hooks
  const myUid = user?.id ?? '';
  const myCompanionDocId = useMemo(() => {
    if (!activePetOwnerUid || !activePet?.id) return null;
    return `${activePetOwnerUid}_${activePet.id}`;
  }, [activePetOwnerUid, activePet?.id]);

  // Scroll the user's own companion row into view when the list renders
  useEffect(() => {
    if (!myRowRef.current) return;
    myRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [companions, companionsWeek, companionTab]);

  useEffect(() => {
    const el = document.getElementById('main-scroll');
    if (!el) return;
    const onScroll = () => {
      setShowScrollTop(el.scrollTop > 720);
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const hasNoPetProfiles = !petsLoading && pets.length === 0;

  if (hasNoPetProfiles) {
    return (
      <div className="w-full max-w-lg mx-auto px-4 pb-24 pt-6 min-h-screen">
        <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white border border-gray-200 flex items-center justify-center shadow-sm">
            <BeanIcon width={34} height={34} className="opacity-50" />
          </div>
          <h2 className="text-lg font-black text-[#0d2744] mb-2">{t('companions.pageTitle')}</h2>
          <p className="text-sm text-gray-600">
            Add a pet profile first to unlock Fellowship progress, streaks, and companions.
          </p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-brown/20 border-t-primary-brown rounded-full animate-spin" />
      </div>
    );
  }

  const level = levelFromXP(profile.xp_total);
  const accent = activePet?.color || profile.roomie_color || PET_COLORS[0];
  const surfaceBorder = accent + '33';
  const petDisplayName = activePet?.name?.trim() || profile.roomie_nickname || user?.full_name?.split(' ')[0] || null;
  const isPublic = profile.public_view !== false;

  return (
    <div className="w-full max-w-lg mx-auto px-4 pb-24 pt-6 space-y-5 min-h-screen">

      {/* Page title */}
      <h1 className="text-xl font-black text-[#0d2744] mb-2 mt-1">
        {t('companions.pageTitle')}
      </h1>

      {/* Header Card */}
      <div className="rounded-3xl p-6" style={{ background: accent + '18', border: `1.5px solid ${surfaceBorder}` }}>
        <div className="flex items-center gap-4 mb-5">
          <div className="w-16 h-16 shrink-0 rounded-full flex items-center justify-center" style={{ background: accent + '22' }}>
            {activePet?.species
              ? <SpeciesGlyph species={activePet.species} color={accent} className="w-11 h-11" />
              : <BeanAvatar color={accent} size={64} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold tracking-widest uppercase mb-0.5" style={{ color: accent }}>
              {t('bean.level')} {level}
            </p>
            <h1 className="text-xl font-black text-[#0d2744] truncate">
              {petDisplayName ?? (
                <span className="text-[#0d2744]/30 italic font-semibold">Your Pet</span>
              )}
            </h1>
            <p className="text-sm" style={{ color: accent + 'aa' }}>{t(LEVEL_NAME_KEYS[level - 1] ?? 'bean.levelName.legend')}</p>
          </div>
        </div>
        <XPBar xp={profile.xp_total} level={level} color={accent} />
      </div>

      {/* Streak Card */}
      <div className="rounded-3xl p-6" data-tour="streak-card" style={{ background: accent + '18' }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-[#0d2744]">{t('bean.dailyStreak')}</h2>
          <div className="flex items-center gap-1.5">
            <Bone size={22} style={{ color: accent }} />
            <span className="text-2xl font-black text-[#0d2744]">{computedStreak}</span>
          </div>
        </div>
        <div className="flex gap-6 text-center mb-4">
          <div>
            <p className="text-2xl font-black" style={{ color: accent }}>{computedStreak}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('bean.current')}</p>
          </div>
          <div className="w-px" style={{ background: surfaceBorder }} />
          <div>
            <p className="text-2xl font-black text-[#0d2744]">{profile.longest_streak}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('bean.best')}</p>
          </div>
          <div className="w-px" style={{ background: surfaceBorder }} />
          <div>
            <p className="text-2xl font-black text-[#0d2744]">{profile.xp_total}</p>
            <p className="text-xs text-gray-400 mt-0.5">{t('bean.totalXP')}</p>
          </div>
        </div>
        {!(computedStreak) && (
          <p className="text-sm text-center py-2 mb-2" style={{ color: accent }}>
            {t('bean.streakZero')}
          </p>
        )}
        <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: accent + '88' }}>
          {t('bean.last30Days')}
        </p>
        <StreakCalendar logs={loggedDates} color={accent} />
      </div>

      {/* Companionship Card */}
      <div className="rounded-3xl p-6" style={{ background: accent + '18' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Trophy size={20} style={{ color: accent }} />
            <h2 className="text-base font-bold text-[#0d2744]">{t('companions.title')}</h2>
          </div>
          {isPublic ? (
            <span className="text-xs font-bold text-positive-green bg-green-50 px-2 py-0.5 rounded-full">
              {t('bean.active')}
            </span>
          ) : (
            <span className="text-xs font-semibold text-gray-400">{t('companions.publicViewOff')}</span>
          )}
          <button
            onClick={handlePublicToggle}
            disabled={savingPublic || !profile}
            aria-label={t('companions.publicView')}
            className={cn(
              'relative w-10 h-5 rounded-full transition-colors shrink-0',
              isPublic ? 'bg-primary-brown' : 'bg-gray-200',
              (savingPublic || !profile) && 'opacity-60 cursor-default'
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 left-0 w-4 h-4 bg-white rounded-full shadow transition-transform',
                isPublic ? 'translate-x-[22px]' : 'translate-x-0.5'
              )}
            />
          </button>
        </div>
        <p className="text-xs text-text-secondary mb-3">
          {companionTab === 'week' ? t('companions.leaderboardHintWeek') : t('companions.leaderboardHint')}
        </p>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 p-1 rounded-full bg-white/60 border" style={{ borderColor: surfaceBorder }}>
          {(['all', 'week'] as const).map(tab => {
            const active = companionTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setCompanionTab(tab)}
                className={`flex-1 text-xs font-bold py-1.5 rounded-full transition-all ${active ? 'text-white shadow-sm' : 'text-text-secondary hover:text-text-primary'}`}
                style={active ? { background: accent } : undefined}
              >
                {tab === 'all' ? t('companions.tabAll') : t('companions.tabWeek')}
              </button>
            );
          })}
        </div>

        {/* Companion Story picker */}
        {(() => {
          return null; // moved to megaphone popup modal
        })()}

        {!isPublic && (
          <div className="mb-4 px-3 py-2.5 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-xs">
            {t('companions.hiddenNotice')}
          </div>
        )}

        {(() => {
          const list = companionTab === 'week' ? companionsWeek : companions;
          if (list.length === 0) {
            return (
              <div className="text-center py-8">
                <p className="text-4xl mb-3">{companionTab === 'week' ? '⚡' : '🏆'}</p>
                <p className="text-sm text-text-secondary">
                  {companionTab === 'week' ? t('companions.leaderboardEmptyWeek') : t('companions.leaderboardEmpty')}
                </p>
              </div>
            );
          }
          return (
            <div>
              {list.map((entry, i) => {
                const isMe = entry.user_id === myUid;
                const isActivePetRow = myCompanionDocId != null && entry.id === myCompanionDocId;
                return (
                  <div key={entry.id} ref={isActivePetRow ? myRowRef : undefined}>
                    <CompanionRow
                      entry={entry}
                      rank={i + 1}
                      isMe={isMe}
                      currentUserId={myUid}
                      onReact={reactToCompanion}
                      onStoryEdit={isActivePetRow ? () => {
                        setDraftChip(profile.story_chip ?? null);
                        setDraftText(profile.story_text ?? '');
                        setStoryModalOpen(true);
                      } : undefined}
                      showWeek={companionTab === 'week'}
                      t={t}
                    />
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Companion Story modal */}
      <Modal open={storyModalOpen} title={t('companions.story.modalTitle')} onClose={() => setStoryModalOpen(false)}>
        <div className="flex flex-col gap-4 p-1">
          <p className="text-sm text-text-secondary">{t('companions.story.hint')}</p>

          {/* Chip grid */}
          <div className="flex flex-wrap gap-2">
            {STORY_CHIPS.filter(c => c.key !== 'none').map(chip => {
              const active = draftChip === chip.key;
              const ChipIcon = STORY_CHIP_ICONS[chip.key];
              return (
                <button
                  key={chip.key}
                  onClick={() => setDraftChip(active ? null : chip.key)}
                  className={`text-[12px] font-semibold px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${
                    active ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-white border-gray-200 text-text-secondary hover:border-amber-200'
                  }`}
                >
                  {ChipIcon && <ChipIcon size={13} aria-hidden />}
                  <span>{t(`companions.story.${chip.key}` as TranslationKey)}</span>
                </button>
              );
            })}
          </div>

          {/* Free-text */}
          <div className="relative">
            <textarea
              value={draftText}
              onChange={e => setDraftText(e.target.value.slice(0, STORY_TEXT_MAX_LEN))}
              placeholder={t('companions.story.placeholder')}
              rows={3}
              className="w-full rounded-2xl border border-gray-200 bg-gray-50 p-3 text-sm text-text-primary resize-none focus:outline-none focus:ring-2 focus:ring-primary-brown/30"
            />
            <span className="absolute bottom-2 right-3 text-[10px] text-gray-400">
              {draftText.length}/{STORY_TEXT_MAX_LEN}
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={async () => {
                if (savingStory) return;
                setSavingStory(true);
                try {
                  await setStory(draftChip, draftText.trim() || null);
                  setStoryModalOpen(false);
                } finally {
                  setSavingStory(false);
                }
              }}
              disabled={savingStory}
              className="flex-1 py-2.5 rounded-full text-sm font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
              style={{ background: accent }}
            >
              {savingStory ? '…' : t('companions.story.save')}
            </button>
            {(profile.story_chip || (profile.story_text ?? '').length > 0) && (
              <button
                onClick={async () => {
                  if (savingStory) return;
                  setSavingStory(true);
                  try {
                    await setStory(null, null);
                    setDraftChip(null);
                    setDraftText('');
                    setStoryModalOpen(false);
                  } finally {
                    setSavingStory(false);
                  }
                }}
                disabled={savingStory}
                className="px-4 py-2.5 rounded-full text-sm font-bold border border-gray-200 text-text-secondary hover:border-error-red\/20 disabled:opacity-60"
              >
                {t('companions.story.clear')}
              </button>
            )}
          </div>
        </div>
      </Modal>

      {pendingCelebration && (
        <CelebrationModal onClose={clearCelebration} />
      )}

      {showScrollTop && (
        <button
          onClick={() => document.getElementById('main-scroll')?.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-4 z-40 w-11 h-11 rounded-full shadow-md border border-white/70 text-white flex items-center justify-center transition-opacity hover:opacity-90"
          style={{ background: accent }}
          aria-label="Scroll to top"
          title="Scroll to top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
}
